/**
 * Azure Document Intelligence — reading an image or a scanned PDF (2026-09-17).
 *
 * ---- What it is for ----
 *
 * The readers in `readUploadedFile.ts` open files that CARRY structure: workbook rows, Word
 * tables, a PDF's text layer. A photographed requisition, a screenshot, or a scan carries
 * pixels. Akhil, 2026-09-17: "if I upload an image with all the items, will it extract those
 * items?" — it did not, and the one server-side extractor that could have (`mail.
 * extract_enquiry_items`, B1) has been dead since August. This is the layer ABOVE the existing
 * readers, not a change to them: the `prebuilt-layout` model does the seeing — OCR plus real
 * table structure — and hands back `tables[]` of cells, which `diSheets` lays out as the same
 * `LegacySheet` rows a workbook produces. From there an image is read exactly like an Excel
 * sheet: `readItemsFromWorkbook`'s header detection and column inference, `verifyUoms`, the
 * matcher. A page with no table gives `diText` for the prose path (`mailBodyExtract.ts`).
 *
 * ---- Where the key lives ----
 *
 * Never here. The browser calls the same-origin `/di/*` path; `server/index.ts` (production) and
 * `vite.config.ts` (dev) attach `Ocp-Apim-Subscription-Key` from `AZURE_DI_KEY` on the way out,
 * the way `/search` and `/llm` already carry their tokens. Unset, the route is absent and
 * `visionAvailable` answers false, so the picker says images are not read on this deployment.
 *
 * ---- The call is asynchronous ----
 *
 * `POST …:analyze` answers 202 with an `Operation-Location` to poll; the result arrives when
 * `status` is `succeeded`. `diPollPath` rewrites that absolute Azure URL into our `/di` path —
 * and refuses anything that is not an `analyzeResults` location, so a response cannot steer the
 * browser to poll an arbitrary host through the proxy.
 *
 * This runs on our proxy rather than on Frappe, which `CLAUDE.md` assigns AI extraction to — a
 * deliberate deviation while B1 stays broken, and offered to the Frappe team as the reference
 * to port. Deterministic OCR; the LLM step, when reached, is the existing mail-body reader.
 */

import { currentClerkToken } from "./api/frappeSession";
import { findHeaderRow } from "./sheetExtract";
import type { LegacySheet } from "./legacyXls";

const DI_PATH = "/di";
const API_VERSION = "2024-11-30";
const MODEL = "prebuilt-layout";

/** The slice of an `analyzeResult` this module reads. Everything else Azure returns is ignored. */
export interface DiAnalyzeResult {
  content?: string;
  tables?: Array<{
    rowCount: number;
    columnCount: number;
    cells: Array<{
      rowIndex: number;
      columnIndex: number;
      content?: string;
      rowSpan?: number;
      columnSpan?: number;
      kind?: string;
    }>;
  }>;
}

/**
 * Every table as a sheet of rows — absent cells `null`, merged cells spread over their span.
 *
 * A table DI split at a page break is joined back: a later table with the SAME column count and
 * no heading row of its own continues the headed table before it (199E-edited.pdf, 2026-09-22 —
 * lines 6–8 on page 2 came back as their own table and, read by shape, lost their serials).
 */
export function diSheets(result: DiAnalyzeResult): LegacySheet[] {
  const sheets = (result.tables ?? []).map((table, t) => ({
    ...gridOf(table),
    name: `Table ${t + 1}`,
    columns: table.columnCount,
  }));
  const out: (LegacySheet & { columns: number })[] = [];
  for (const sheet of sheets) {
    const previous = out[out.length - 1];
    const continues =
      previous !== undefined &&
      previous.columns === sheet.columns &&
      findHeaderRow(previous.rows) >= 0 &&
      findHeaderRow(sheet.rows) !== 0;
    if (continues) previous.rows.push(...sheet.rows);
    else out.push(sheet);
  }
  return out.map(({ name, rows }) => ({ name, rows }));
}

function gridOf(table: NonNullable<DiAnalyzeResult["tables"]>[number]): {
  rows: LegacySheet["rows"];
} {
  const rows: LegacySheet["rows"] = Array.from({ length: table.rowCount }, () =>
    Array<string | null>(table.columnCount).fill(null),
  );
  for (const cell of table.cells) {
    const text = (cell.content ?? "").replace(/\s+/g, " ").trim();
    const value = text === "" ? null : text;
    for (
      let r = cell.rowIndex;
      r < cell.rowIndex + (cell.rowSpan ?? 1) && r < table.rowCount;
      r++
    ) {
      for (
        let c = cell.columnIndex;
        c < cell.columnIndex + (cell.columnSpan ?? 1) && c < table.columnCount;
        c++
      ) {
        rows[r][c] = value;
      }
    }
  }
  return { rows };
}

/** The page text, for a list written as prose rather than a table. */
export function diText(result: DiAnalyzeResult): string {
  return result.content ?? "";
}

/** Azure's absolute `Operation-Location` → our same-origin poll path; `null` for anything else. */
export function diPollPath(operationLocation: string): string | null {
  const m =
    /\/documentintelligence(\/documentModels\/[A-Za-z0-9._-]+\/analyzeResults\/[A-Za-z0-9-]+(?:\?[^#]*)?)$/.exec(
      operationLocation,
    );
  return m ? `${DI_PATH}${m[1]}` : null;
}

async function authHeaders(): Promise<Record<string, string>> {
  const bearer = await currentClerkToken();
  return bearer ? { Authorization: `Bearer ${bearer}` } : {};
}

/** Whether this deployment reads images at all — the `/di` proxy is optional. */
export async function visionAvailable(): Promise<boolean> {
  try {
    const res = await fetch(`${DI_PATH}/info?api-version=${API_VERSION}`, {
      headers: await authHeaders(),
    });
    // The dev server's SPA fallback answers an unproxied path with the HTML shell — 200, but not JSON.
    return res.ok && (res.headers.get("content-type") ?? "").includes("json");
  } catch {
    return false;
  }
}

const toBase64 = (bytes: Uint8Array): string => {
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000)
    s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Read one file with the layout model. Throws on transport or shape failure so the caller can
 * say so — an image that could not be read is not an order for nothing.
 */
export async function analyzeLayout(
  bytes: Uint8Array,
  options: { timeoutMs?: number } = {},
): Promise<DiAnalyzeResult> {
  const headers = {
    "Content-Type": "application/json",
    ...(await authHeaders()),
  };
  const submit = await fetch(
    `${DI_PATH}/documentModels/${MODEL}:analyze?api-version=${API_VERSION}`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({ base64Source: toBase64(bytes) }),
    },
  );
  if (submit.status !== 202)
    throw new Error(
      `Document Intelligence refused the file (${submit.status})`,
    );
  const poll = diPollPath(submit.headers.get("operation-location") ?? "");
  if (!poll) throw new Error("Document Intelligence gave no result location");

  const deadline = Date.now() + (options.timeoutMs ?? 120_000);
  while (Date.now() < deadline) {
    await sleep(1_000);
    const res = await fetch(poll, { headers: await authHeaders() });
    if (!res.ok)
      throw new Error(
        `Document Intelligence result unavailable (${res.status})`,
      );
    const body = (await res.json()) as {
      status?: string;
      analyzeResult?: DiAnalyzeResult;
      error?: { message?: string };
    };
    if (body.status === "succeeded") return body.analyzeResult ?? {};
    if (body.status === "failed")
      throw new Error(
        body.error?.message ?? "Document Intelligence could not read the file",
      );
  }
  throw new Error(
    "Document Intelligence took too long — try a smaller or clearer image",
  );
}
